import forge from "node-forge";
import { CRYPTO_CONSTANTS } from "./cryptoConstants";

/**
 * CryptoProvider - a wrapper around node-forge that provides a Bouncy Castle-like interface
 * for frontend encryption operations.
 *
 * This helps standardize the cryptographic operations across the frontend similar to
 * how Bouncy Castle works in Java/Android.
 */
class CryptoProvider {
  private static instance: CryptoProvider;
  private initialized = false;
  private provider: string = CRYPTO_CONSTANTS.PROVIDER_NAME;

  private constructor() {
    this.initialize();
  }

  public static getInstance(): CryptoProvider {
    if (!CryptoProvider.instance) {
      CryptoProvider.instance = new CryptoProvider();
    }
    return CryptoProvider.instance;
  }

  /**
   * Initialize the crypto provider
   */
  private initialize(): boolean {
    try {
      // Verify the node-forge library is correctly loaded
      if (!forge || !forge.pki || !forge.md || !forge.util) {
        console.error("Failed to initialize node-forge crypto provider");
        this.initialized = false;
        return false;
      }
      this.initialized = true;
      return true;
    } catch (error) {
      console.error("Error initializing crypto provider:", error);
      this.initialized = false;
      return false;
    }
  }

  /**
   * Check if the provider is initialized
   */
  public isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Convert a PEM-formatted public key to a Forge public key
   * Similar to Bouncy Castle's publicKeyFromPem
   */
  public publicKeyFromPem(pem: string): forge.pki.rsa.PublicKey {
    return forge.pki.publicKeyFromPem(pem);
  }

  /**
   * Convert a Base64-encoded DER public key to a Forge public key
   * Similar to Bouncy Castle's publicKeyFromDer
   */
  public publicKeyFromBase64(base64Key: string): forge.pki.rsa.PublicKey {
    try {
      const derBytes = forge.util.decode64(base64Key);
      const asn1 = forge.asn1.fromDer(derBytes);
      return forge.pki.publicKeyFromAsn1(asn1);
    } catch (error) {
      console.error("Error decoding public key from Base64:", error);
      throw error;
    }
  }

  /**
   * Generate the MessageDigest for the specified algorithm
   * Similar to Bouncy Castle's MessageDigest.getInstance()
   */
  public getMessageDigest(
    algorithm: "sha1" | "sha256" | "sha512" | "md5"
  ): forge.md.MessageDigest {
    switch (algorithm) {
      case "sha1":
        return forge.md.sha1.create();
      case "sha256":
        return forge.md.sha256.create();
      case "sha512":
        return forge.md.sha512.create();
      case "md5":
        return forge.md.md5.create();
      default:
        throw new Error(`Unsupported digest algorithm: ${algorithm}`);
    }
  }

  /**
   * Encrypt data using RSA with the specified algorithm and padding
   * Similar to Bouncy Castle's Cipher.doFinal()
   */
  public rsaEncrypt(
    data: string,
    publicKey: forge.pki.rsa.PublicKey,
    algorithm:
      | "sha256"
      | "sha1"
      | "pkcs1" = CRYPTO_CONSTANTS.DEFAULT_HASH_ALGORITHM as "sha256"
  ): string {
    try {
      // Convert the data to UTF-8 encoding
      const dataUtf8 = forge.util.encodeUtf8(data);

      let encryptedData: string;

      // Choose the encryption method based on the algorithm parameter
      if (algorithm === "pkcs1") {
        encryptedData = publicKey.encrypt(
          dataUtf8,
          CRYPTO_CONSTANTS.ENCRYPTION_PADDING_PKCS1
        );
      } else {
        // Use OAEP padding with the specified hash algorithm
        const hashAlgo =
          algorithm === "sha256"
            ? forge.md.sha256.create()
            : forge.md.sha1.create();

        encryptedData = publicKey.encrypt(
          dataUtf8,
          CRYPTO_CONSTANTS.ENCRYPTION_PADDING_SHA256,
          {
            md: hashAlgo,
            mgf1: {
              md: hashAlgo,
            },
          }
        );
      }

      // Encode the encrypted data to URL-safe Base64
      // This is essential for compatibility with backend systems
      return forge.util.encode64(encryptedData);
      // .replace(/\+/g, "-")
      // .replace(/\//g, "_")
      // .replace(/=+$/, "");
    } catch (error) {
      console.error(`Error encrypting data with ${algorithm}:`, error);
      throw error;
    }
  }
}

// Export a singleton instance
export const cryptoProvider = CryptoProvider.getInstance();
